import apiClient from './client';
import { ApiResponse } from '@/types';
import { GatewayOutcome } from './agents.api';

/**
 * Issue Memory: the shared state of each GitLab issue (plan §4.5).
 */

export type IssueStatus =
  | 'todo'
  | 'in-progress'
  | 'in-review'
  | 'test'
  | 'blocked'
  | 'needs-info'
  | 'needs-evidence'
  | 'done';

export interface IssueSummary {
  ref: string;
  gitlabProjectId: number;
  projectPath?: string;
  iid: number;
  title: string;
  state: 'opened' | 'closed';
  status: IssueStatus;
  assignees: string[];
  labels: string[];
  milestone?: string;
  dueDate?: string;
  waitingOn?: { username: string; since: string; question: string };
  openQuestions: number;
  openBlockers: number;
  mrs: Array<{ iid: number; gitlabProjectId: number; state?: string; draft?: boolean; pipeline?: string; reviewApp?: string }>;
  lastHumanActivityAt?: string;
  atRisk?: boolean;
  dod?: { passed: boolean; missing: number };
  revision: number;
  updatedAt: string;
  webUrl?: string;
}

export interface IssueMemoryRecord {
  id: string;
  gitlabProjectId: number;
  iid: number;
  projectPath?: string;
  title: string;
  webUrl?: string;
  state: 'opened' | 'closed';
  status: IssueStatus;
  labels: string[];
  author?: string;
  assignees: string[];
  milestone?: string;
  dueDate?: string;
  closedAt?: string;
  goal?: string;
  acceptance: Array<{ text: string; done: boolean }>;
  summary?: string;
  howToTest?: string;
  decisions: Array<{ text: string; by?: string; at: string; sourceUrl?: string }>;
  questions: Array<{
    qid: string;
    text: string;
    askedTo?: string;
    askedBy?: string;
    askedAt: string;
    answer?: string;
    answeredBy?: string;
    answeredAt?: string;
    answerUrl?: string;
  }>;
  waitingOn?: { username: string; since: string; question: string };
  blockers: Array<{ bid: string; text: string; by?: string; at: string; clearedAt?: string; clearedBy?: string }>;
  progress: Array<{ text: string; by?: string; at: string }>;
  filesTouched: string[];
  branches: Array<{ gitlabProjectId: number; projectPath?: string; name: string }>;
  mrs: Array<{
    gitlabProjectId: number;
    projectPath?: string;
    iid: number;
    title?: string;
    state?: string;
    draft?: boolean;
    sourceBranch?: string;
    targetBranch?: string;
    webUrl?: string;
    closing?: boolean;
    pipeline?: { id?: number; status?: string; at?: string };
    reviewApp?: { environment?: string; url?: string; status?: string; at?: string };
  }>;
  evidence: Array<{ mrIid?: number; verdict: 'pass' | 'fail'; summary: string; by?: string; at: string; sourceUrl?: string }>;
  sessions: Array<{
    sessionId: string;
    agent?: string;
    model?: string;
    startedAt?: string;
    lastActiveAt?: string;
    endedAt?: string;
    turns?: number;
    costUsd?: number;
    summary?: string;
  }>;
  recentComments?: Array<{ by?: string; at: string; text: string; url?: string }>;
  dod?: {
    profile: string;
    passed: boolean;
    missing: string[];
    warnings: string[];
    checkedAt: string;
    failures: number;
    override?: { by: string; at: string; reason: string };
  };
  lastHumanActivityAt?: string;
  lastBotActivityAt?: string;
  memoryNote?: { noteId?: number; syncedAt?: string; mode?: string };
  revision: number;
  updatedAt: string;
}

export interface RelatedIssue {
  ref: string;
  title: string;
  state: string;
  score: number;
  reasons: string[];
}

export interface HistoryEntry {
  revision: number;
  at: string;
  by?: string;
  changes: string[];
}

export interface ApiToken {
  id: string;
  name: string;
  kind: 'person' | 'agent';
  hint: string;
  username: string;
  role: string;
  scopes: string[];
  createdBy?: string;
  createdAt: string;
  lastUsedAt?: string;
  expiresAt?: string;
  revokedAt?: string;
}

export interface IssueListParams {
  q?: string;
  project?: string;
  state?: 'opened' | 'closed' | 'all';
  assignee?: string;
  status?: string;
  limit?: number;
}

const issuePath = (gitlabProjectId: number | string, iid: number | string) =>
  `/issue-memory/${encodeURIComponent(String(gitlabProjectId))}/${iid}`;

export async function listIssues(params: IssueListParams = {}): Promise<IssueSummary[]> {
  const response = await apiClient.get<ApiResponse<IssueSummary[]>>('/issue-memory', { params });
  return response.data.data || [];
}

export async function getIssue(gitlabProjectId: number | string, iid: number | string): Promise<IssueMemoryRecord> {
  const response = await apiClient.get<ApiResponse<IssueMemoryRecord>>(issuePath(gitlabProjectId, iid));
  return response.data.data as IssueMemoryRecord;
}

/** The markdown agents and Claude Code sessions receive */
export async function getDossier(gitlabProjectId: number | string, iid: number | string): Promise<string> {
  const response = await apiClient.get<string>(issuePath(gitlabProjectId, iid), {
    params: { format: 'markdown' },
    responseType: 'text',
  });
  return response.data;
}

export async function getRelated(gitlabProjectId: number | string, iid: number | string): Promise<RelatedIssue[]> {
  const response = await apiClient.get<ApiResponse<RelatedIssue[]>>(`${issuePath(gitlabProjectId, iid)}/related`);
  return response.data.data || [];
}

export async function getHistory(gitlabProjectId: number | string, iid: number | string): Promise<HistoryEntry[]> {
  const response = await apiClient.get<ApiResponse<HistoryEntry[]>>(`${issuePath(gitlabProjectId, iid)}/history`);
  return response.data.data || [];
}

export async function listTokens(): Promise<ApiToken[]> {
  const response = await apiClient.get<ApiResponse<ApiToken[]>>('/issue-memory/tokens');
  return response.data.data || [];
}

export async function createToken(input: {
  name: string;
  kind?: 'person' | 'agent';
  username?: string;
  readOnly?: boolean;
  runner?: boolean;
  expiresInDays?: number;
}): Promise<ApiToken & { token: string }> {
  const response = await apiClient.post<ApiResponse<ApiToken & { token: string }>>('/issue-memory/tokens', input);
  return response.data.data as ApiToken & { token: string };
}

export async function revokeToken(id: string): Promise<void> {
  await apiClient.delete(`/issue-memory/tokens/${id}`);
}

export interface AssignableMember {
  username: string;
  name: string;
  ai: boolean; // the AI developer
}

/** Executives and PMs: who can be assigned in the issue's project */
export async function getAssignable(gitlabProjectId: number | string, iid: number | string): Promise<AssignableMember[]> {
  const response = await apiClient.get<ApiResponse<AssignableMember[]>>(`${issuePath(gitlabProjectId, iid)}/assignable`);
  return response.data.data || [];
}

/** Assign (username null: unassign) through the manager; the memory follows when GitLab's webhook comes back */
export async function assignIssue(gitlabProjectId: number | string, iid: number | string, username: string | null): Promise<GatewayOutcome> {
  const response = await apiClient.post<ApiResponse<GatewayOutcome>>(`${issuePath(gitlabProjectId, iid)}/assign`, { username });
  return response.data.data as GatewayOutcome;
}

/** Executives and PMs: write the criteria into the description's "Acceptance criteria" section */
export async function setAcceptance(
  gitlabProjectId: number | string,
  iid: number | string,
  items: Array<{ text: string; done: boolean }>
): Promise<GatewayOutcome | { decision: 'unchanged' }> {
  const response = await apiClient.put<ApiResponse<GatewayOutcome | { decision: 'unchanged' }>>(`${issuePath(gitlabProjectId, iid)}/acceptance`, { items });
  return response.data.data as GatewayOutcome | { decision: 'unchanged' };
}

/** Executives and PMs: comment in GitLab (posted by the manager with your name, handled as yours) */
export async function addComment(gitlabProjectId: number | string, iid: number | string, body: string): Promise<GatewayOutcome> {
  const response = await apiClient.post<ApiResponse<GatewayOutcome>>(`${issuePath(gitlabProjectId, iid)}/comments`, { body });
  return response.data.data as GatewayOutcome;
}

/** Executives and PMs: write the goal into the description ("What" section or first paragraph) */
export async function setGoal(gitlabProjectId: number | string, iid: number | string, goal: string): Promise<GatewayOutcome | { decision: 'unchanged' }> {
  const response = await apiClient.put<ApiResponse<GatewayOutcome | { decision: 'unchanged' }>>(`${issuePath(gitlabProjectId, iid)}/goal`, { goal });
  return response.data.data as GatewayOutcome | { decision: 'unchanged' };
}

export type AiHelp = 'AI developer' | 'AI QA' | 'AI review' | 'Claude Code (person)';

export interface OverviewIssue {
  ref: string;
  gitlabProjectId: number;
  projectPath?: string;
  iid: number;
  title: string;
  webUrl?: string;
  state: 'opened' | 'closed';
  status: IssueStatus;
  assignees: string[];
  opened: boolean;
  closed: boolean;
  closedAt?: string;
  ai: AiHelp[];
  aiEver: boolean;
  dodReopens: number;
  lastActivityAt?: string;
}

export interface OverviewCounts {
  worked: number;
  opened: number;
  closed: number;
  withAi: number;
  closedWithAi: number;
  mergedMrs: number;
  dodReopens: number;
}

export interface TeamOverview {
  since: string;
  until: string;
  days: number;
  totals: OverviewCounts & { aiSessions: number; aiTurns: number; aiCostUsd?: number };
  projects: Array<OverviewCounts & { gitlabProjectId: number; projectPath?: string }>;
  people: Array<{ username: string; ai: boolean; worked: number; closed: number }>;
  aiSessions: { byRole: Record<string, number>; byProfile: Record<string, number> };
  issues: OverviewIssue[];
}

/** Executives and PMs: the last `days` days (issues worked, closed, done with AI) */
export async function getOverview(days: number): Promise<TeamOverview> {
  const response = await apiClient.get<ApiResponse<TeamOverview>>('/issue-memory/overview', { params: { days } });
  return response.data.data as TeamOverview;
}
